import { Controller } from "@hotwired/stimulus"

// Phase 2.12 Tier 4 Step 34/35 — Buildots-style PM commentary
// inline-edit. Used in two surfaces:
//
//   1. The portfolio table's `pm_note` column (one input per row).
//   2. The project dashboard's "What's the story this week?" tile.
//
// Same controller, same backend (`PATCH /projects/:project_id/dashboard_note`).
// Save-on-blur and debounced save-on-input — owners stay in flow without a
// "Save" button. 280-char ceiling enforced client and server-side.
//
// Anchored on a wrapper `<div data-controller="dashboard-note">` with one
// `dashboard-note.input` target. The wrapper carries:
//
//   data-dashboard-note-url-value   — PATCH endpoint
//   data-dashboard-note-debounce-value — ms (default 600)
//
// Mixpanel: `Dashboard Note Updated` is server-side (sampled 10%); we
// don't fire client-side to avoid double-counting.
export default class extends Controller {
  static targets = ["input", "status"]
  static values  = {
    url:      String,
    debounce: { type: Number, default: 600 }
  }

  connect() {
    this._timer = null
    this._lastSaved = this.inputTarget?.value ?? ""
  }

  disconnect() {
    if (this._timer) clearTimeout(this._timer)
  }

  // Stop the row click handler from navigating to the project detail
  // page when the user clicks into the input. The portfolio table row
  // wrapper carries `data-action="click->portfolio-dashboard#navigateToProject"`.
  swallowClick(event) {
    event.stopPropagation()
  }

  inputChanged() {
    if (this._timer) clearTimeout(this._timer)
    this._timer = setTimeout(() => this.save(), this.debounceValue)
    this._setStatus("typing")
  }

  // Immediate save on blur. Skip if nothing changed since the last save.
  save() {
    if (this._timer) {
      clearTimeout(this._timer)
      this._timer = null
    }

    const body = (this.inputTarget?.value ?? "").slice(0, 280)
    if (body === this._lastSaved) {
      this._setStatus("idle")
      return
    }

    if (!this.urlValue) {
      this._setStatus("error")
      return
    }

    this._setStatus("saving")
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrf
      },
      credentials: "same-origin",
      body: JSON.stringify({ body })
    })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(payload => {
        this._lastSaved = payload.body || ""
        this._setStatus("saved")
        setTimeout(() => this._setStatus("idle"), 1500)
      })
      .catch(() => this._setStatus("error"))
  }

  _setStatus(state) {
    if (!this.hasStatusTarget) return

    const labels = {
      idle:   "",
      typing: "Editing…",
      saving: "Saving…",
      saved:  "Saved",
      error:  "Save failed"
    }

    this.statusTarget.textContent = labels[state] ?? ""
    this.statusTarget.classList.toggle("text-emerald-600", state === "saved")
    this.statusTarget.classList.toggle("text-red-600",     state === "error")
    this.statusTarget.classList.toggle("text-gray-400",    state === "typing" || state === "saving" || state === "idle")
  }
}
